/**
 * STORE GLOBAL ZUSTAND UNIFIÉ (OPTIMISÉ)
 *
 * Facade légère exportant des hooks atomiques vers les différents stores de l'application.
 * Évite la duplication d'état et les re-renders excessifs.
 *
 * @version 2.0.0
 * @since Sprint 8 - Optimisation Zustand
 */

import { shallow } from 'zustand/shallow';
import { useAuthStore } from './authStore';
import { usePresenceStore } from './presenceStore';
import { create } from 'zustand';

// === DÉFINITIONS DE TYPES ===

export interface Consultation {
  id: number;
  patientId: number;
  doctorId: number;
  status: 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  type: 'REGULAR' | 'EMERGENCY' | 'FOLLOW_UP';
  notes?: string;
  scheduledAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Notification {
  id: string;
  type: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  userId: number;
}

// TODO: Extraire Consultations, Notifications, Realtime et UI dans leurs propres fichiers (Zustand slices)
// Pour l'instant, on maintient la compatibilité en gardant un mini-store pour ces éléments

interface LegacyGlobalState {
  consultations: {
    activeConsultation: Consultation | null;
    pendingConsultations: Consultation[];
    completedConsultations: Consultation[];
    isLoading: boolean;
    error: string | null;
  };
  notifications: {
    list: Notification[];
    unreadCount: number;
    isLoading: boolean;
    error: string | null;
  };
  realtime: {
    isConnected: boolean;
    connectionError: string | null;
    lastMessage: any;
  };
  ui: {
    theme: 'light' | 'dark';
    sidebarOpen: boolean;
    activeView: string;
  };
}

interface LegacyGlobalActions {
  consultationActions: {
    setActiveConsultation: (consultation: Consultation | null) => void;
    addPendingConsultation: (consultation: Consultation) => void;
    completeConsultation: (consultationId: number) => void;
    cancelConsultation: (consultationId: number) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
  };
  notificationActions: {
    addNotification: (notification: Notification) => void;
    markAsRead: (notificationId: string) => void;
    markAllAsRead: () => void;
    removeNotification: (notificationId: string) => void;
    clearAll: () => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
  };
  realtimeActions: {
    setConnected: (connected: boolean) => void;
    setConnectionError: (error: string | null) => void;
    setLastMessage: (message: any) => void;
  };
  uiActions: {
    setTheme: (theme: 'light' | 'dark') => void;
    toggleSidebar: () => void;
    setActiveView: (view: string) => void;
  };
  initialize: () => void;
  cleanup: () => void;
}

const useLegacyStore = create<LegacyGlobalState & LegacyGlobalActions>()((set) => ({
  consultations: {
    activeConsultation: null,
    pendingConsultations: [],
    completedConsultations: [],
    isLoading: false,
    error: null,
  },
  notifications: {
    list: [],
    unreadCount: 0,
    isLoading: false,
    error: null,
  },
  realtime: {
    isConnected: false,
    connectionError: null,
    lastMessage: null,
  },
  ui: {
    theme: 'light',
    sidebarOpen: true,
    activeView: 'dashboard',
  },

  consultationActions: {
    setActiveConsultation: (consultation) => set((state) => ({ consultations: { ...state.consultations, activeConsultation: consultation } })),
    addPendingConsultation: (consultation) => set((state) => ({ consultations: { ...state.consultations, pendingConsultations: [...state.consultations.pendingConsultations, consultation] } })),
    completeConsultation: (consultationId) => set((state) => {
      const consultation = state.consultations.pendingConsultations.find(c => c.id === consultationId);
      if (!consultation) return state;
      return {
        consultations: {
          ...state.consultations,
          completedConsultations: [...state.consultations.completedConsultations, consultation],
          pendingConsultations: state.consultations.pendingConsultations.filter(c => c.id !== consultationId),
          activeConsultation: state.consultations.activeConsultation?.id === consultationId ? null : state.consultations.activeConsultation,
        }
      };
    }),
    cancelConsultation: (consultationId) => set((state) => ({
      consultations: {
        ...state.consultations,
        pendingConsultations: state.consultations.pendingConsultations.filter(c => c.id !== consultationId),
        activeConsultation: state.consultations.activeConsultation?.id === consultationId ? null : state.consultations.activeConsultation,
      }
    })),
    setLoading: (loading) => set((state) => ({ consultations: { ...state.consultations, isLoading: loading } })),
    setError: (error) => set((state) => ({ consultations: { ...state.consultations, error } })),
  },

  notificationActions: {
    addNotification: (notification) => set((state) => ({
      notifications: {
        ...state.notifications,
        list: [notification, ...state.notifications.list],
        unreadCount: state.notifications.unreadCount + (notification.read ? 0 : 1),
      }
    })),
    markAsRead: (notificationId) => set((state) => ({
      notifications: {
        ...state.notifications,
        list: state.notifications.list.map(n => n.id === notificationId ? { ...n, read: true } : n),
        unreadCount: Math.max(0, state.notifications.unreadCount - 1),
      }
    })),
    markAllAsRead: () => set((state) => ({
      notifications: {
        ...state.notifications,
        list: state.notifications.list.map(n => ({ ...n, read: true })),
        unreadCount: 0,
      }
    })),
    removeNotification: (notificationId) => set((state) => ({
      notifications: {
        ...state.notifications,
        list: state.notifications.list.filter(n => n.id !== notificationId),
        unreadCount: state.notifications.list.find(n => n.id === notificationId)?.read ? state.notifications.unreadCount : Math.max(0, state.notifications.unreadCount - 1),
      }
    })),
    clearAll: () => set({ notifications: { list: [], unreadCount: 0, isLoading: false, error: null } }),
    setLoading: (loading) => set((state) => ({ notifications: { ...state.notifications, isLoading: loading } })),
    setError: (error) => set((state) => ({ notifications: { ...state.notifications, error } })),
  },

  realtimeActions: {
    setConnected: (connected) => set((state) => ({ realtime: { ...state.realtime, isConnected: connected } })),
    setConnectionError: (error) => set((state) => ({ realtime: { ...state.realtime, connectionError: error } })),
    setLastMessage: (message) => set((state) => ({ realtime: { ...state.realtime, lastMessage: message } })),
  },

  uiActions: {
    setTheme: (theme) => set((state) => ({ ui: { ...state.ui, theme } })),
    toggleSidebar: () => set((state) => ({ ui: { ...state.ui, sidebarOpen: !state.ui.sidebarOpen } })),
    setActiveView: (view) => set((state) => ({ ui: { ...state.ui, activeView: view } })),
  },

  initialize: () => {
    usePresenceStore.getState().initialize();
  },
  
  cleanup: () => {
    usePresenceStore.getState().cleanup();
    set({
      consultations: { activeConsultation: null, pendingConsultations: [], completedConsultations: [], isLoading: false, error: null },
      notifications: { list: [], unreadCount: 0, isLoading: false, error: null },
      realtime: { isConnected: false, connectionError: null, lastMessage: null },
      ui: { theme: 'light', sidebarOpen: true, activeView: 'dashboard' },
    });
  }
}));

// ============ HOOKS CONVENIENCE (COMPATIBILITÉ) ============

// Auth hooks (Redirection directe sans duplication)
export const useAuth = () => useAuthStore((state) => ({
  user: state.user,
  token: state.token,
  refreshToken: state.refreshToken,
  isAuthenticated: state.isAuthenticated,
  isLoading: state.isLoading,
  lastActivity: state.lastActivity
}));
export const useAuthActions = () => useAuthStore((state) => ({
  setUser: state.setUser,
  setTokens: state.setTokens,
  updateUser: state.updateUser,
  logout: state.logout,
  setLoading: state.setLoading,
  updateLastActivity: state.updateLastActivity
}));

// Presence hooks
export const usePresence = () => usePresenceStore((state) => ({
  onlineDoctors: state.onlineDoctors,
  onlinePatients: state.onlinePatients,
  stats: state.stats,
  loadingDoctors: state.loadingDoctors,
  loadingPatients: state.loadingPatients,
  loadingStats: state.loadingStats,
  errorDoctors: state.errorDoctors,
  errorPatients: state.errorPatients,
  errorStats: state.errorStats
}));
export const usePresenceActions = () => usePresenceStore((state) => ({
  initialize: state.initialize,
  cleanup: state.cleanup,
  refreshDoctors: state.refreshDoctors,
  refreshPatients: state.refreshPatients,
  refreshAll: state.refreshAll,
  refreshStats: state.refreshStats,
  updateDoctorAvailability: state.updateDoctorAvailability,
  updateUserStatus: state.updateUserStatus,
  removeUser: state.removeUser,
  clearErrors: state.clearErrors
}));

// Consultation hooks
export const useConsultations = () => useLegacyStore((state) => state.consultations);
export const useConsultationActions = () => useLegacyStore((state) => state.consultationActions);

// Notification hooks
export const useNotifications = () => useLegacyStore((state) => state.notifications);
export const useNotificationActions = () => useLegacyStore((state) => state.notificationActions);

// Realtime hooks
export const useRealtime = () => useLegacyStore((state) => state.realtime);
export const useRealtimeActions = () => useLegacyStore((state) => state.realtimeActions);

// UI hooks
export const useUI = () => useLegacyStore((state) => state.ui);
export const useUIActions = () => useLegacyStore((state) => state.uiActions);

// ============ SELECTORS ATOMIQUES FILTRÉS ============

// Auth Selectors
export const useAuthUser = () => useAuthStore((state) => state.user);
export const useAuthToken = () => useAuthStore((state) => state.token);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);

// Presence Selectors
export const useOnlineDoctors = () => usePresenceStore((state) => state.onlineDoctors, shallow);
export const useOnlinePatients = () => usePresenceStore((state) => state.onlinePatients, shallow);
export const usePresenceStats = () => usePresenceStore((state) => state.stats, shallow);

// Consultation Selectors
export const useActiveConsultation = () => useLegacyStore((state) => state.consultations.activeConsultation, shallow);
export const usePendingConsultationList = () => useLegacyStore((state) => state.consultations.pendingConsultations, shallow);
export const useConsultationLoading = () => useLegacyStore((state) => state.consultations.isLoading, shallow);

// Notification Selectors
export const useNotificationListItems = () => useLegacyStore((state) => state.notifications.list, shallow);
export const useUnreadNotificationCount = () => useLegacyStore((state) => state.notifications.unreadCount, shallow);

// Realtime Selectors
export const useSocketIsConnected = () => useLegacyStore((state) => state.realtime.isConnected, shallow);
export const useConnectionError = () => useLegacyStore((state) => state.realtime.connectionError, shallow);

// UI Selectors
export const useTheme = () => useLegacyStore((state) => state.ui.theme, shallow);
export const useSidebarOpen = () => useLegacyStore((state) => state.ui.sidebarOpen, shallow);
export const useActiveView = () => useLegacyStore((state) => state.ui.activeView, shallow);

// ============ SELECTORS OPTIMISÉS COMPLEXES ============

export const useAuthState = useAuth;
export const usePresenceState = usePresence;
export const usePresenceLoadingStates = () => usePresenceStore(
  (state) => ({
    loadingDoctors: state.loadingDoctors,
    loadingPatients: state.loadingPatients,
    loadingStats: state.loadingStats,
  }),
  shallow
);
export const useConsultationState = useConsultations;
export const usePendingConsultations = () => useLegacyStore((state) => state.consultations.pendingConsultations, shallow);
export const useCompletedConsultations = () => useLegacyStore((state) => state.consultations.completedConsultations, shallow);
export const useNotificationState = useNotifications;
export const useNotificationList = () => useLegacyStore((state) => state.notifications.list, shallow);
export const useUIState = useUI;

// ============ ACTIONS GLOBALES (Évaluation Paresseuse) ============

export const globalActions = {
  initialize: () => useLegacyStore.getState().initialize(),
  cleanup: () => useLegacyStore.getState().cleanup(),
  get auth() { return useAuthStore.getState(); },
  get presence() { return usePresenceStore.getState(); },
  get consultations() { return useLegacyStore.getState().consultationActions; },
  get notifications() { return useLegacyStore.getState().notificationActions; },
  get realtime() { return useLegacyStore.getState().realtimeActions; },
  get ui() { return useLegacyStore.getState().uiActions; },
};

// Export pour garder la compatibilité des anciens "useGlobalStore(state => state.auth)"
// (Bien que théoriquement ils devraient être migrés)
export const useGlobalStore = useLegacyStore;
export default useGlobalStore;


